// askSmart-ui/src/pages/TOSP/components/RollsRegister.tsx
import React, { useMemo, useState } from "react";
import DeleteConfirmModal from "../../../components/DeleteConfirmModal";
import InfoModal from "../../../components/InfoModal";
import TaxRollModal from "./TaxRollModal";
import { seedAssignmentsFromUnits, seedTaxRolls } from "../seedTaxRolls";
import { useTospData } from "../TospDataContext";
import {
  activeOccupant,
  currentAllocation,
  newId,
  TaxRollPart,
  TaxRollRecord,
  Unit,
} from "../types";
import { useGridSort } from "../useGridSort";

const GRID_COLUMNS = "1.4fr 0.6fr 0.6fr 0.6fr 1.8fr 1.6fr 1fr";

type SortCol =
  | "rollNumber"
  | "planNumber"
  | "level"
  | "unit"
  | "municipalAddress"
  | "assignedTo";

const COLUMNS: { col: SortCol; label: string }[] = [
  { col: "rollNumber", label: "Roll #" },
  { col: "planNumber", label: "Plan" },
  { col: "level", label: "Level" },
  { col: "unit", label: "Unit" },
  { col: "municipalAddress", label: "Municipal address" },
  { col: "assignedTo", label: "Assigned to" },
];

// The roll register — every assessed parcel the municipality bills, and where
// each one's tax lands.
//
// Shown as the last sub-tab under Tax Rolls because it carries no year: the
// rolls exist first and each year's amounts hang off them. It's the one place
// an unassigned roll is visible, and where a roll is added when the portfolio
// changes — a new store, or a store split in two. Money stays on the year
// tabs, so amounts never live in two places.
export default function RollsRegister() {
  const { taxRolls, units, tenants, saveTaxRolls, deleteTaxRoll } = useTospData();
  const [editing, setEditing] = useState<TaxRollRecord | null | undefined>(undefined);
  const [deleting, setDeleting] = useState<TaxRollRecord | null>(null);
  const [blockedDelete, setBlockedDelete] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  const [planFilter, setPlanFilter] = useState("");

  const unitById = useMemo(() => new Map(units.map((u) => [u.id, u])), [units]);

  /**
   * "Suite 12 · MJ Byrne's Pub", or "" while the part is still in the pool.
   *
   * The tenant rather than the building: admins scan for who's in a space, and
   * the building is already implied by the suite. A roll on a vacant unit says
   * so, which is worth seeing — it's tax with nobody covering it.
   */
  const partLabel = (part: TaxRollPart): string => {
    if (part.assignedTo?.type !== "unit") return "";
    const unit: Unit | undefined = unitById.get(part.assignedTo.id);
    // An assignment pointing at a unit that's since gone is worth showing as a
    // problem rather than as blank, which would read as simply unassigned.
    if (!unit) return "Missing unit";
    const tenant = activeOccupant(unit.id, tenants)?.operatingName;
    return `Suite ${unit.suiteNumber} · ${tenant || "Vacant"}`;
  };

  /** Sorts and filters on the roll as a whole. */
  const assignedLabel = (roll: TaxRollRecord): string =>
    currentAllocation(roll)
      .parts.map((part) => partLabel(part))
      .filter(Boolean)
      .join(", ");

  const isFullyAssigned = (roll: TaxRollRecord): boolean =>
    currentAllocation(roll).parts.every((part) => Boolean(part.assignedTo));

  const plans = useMemo(
    () =>
      Array.from(new Set(taxRolls.map((r) => r.planNumber).filter(Boolean))).sort(
        (a, b) => Number(a) - Number(b),
      ),
    [taxRolls],
  );

  // Counts PARTS, not rolls: a roll split in two with only one side placed is
  // as unbudgetable as a roll nobody has touched.
  const unassignedCount = taxRolls.reduce(
    (total, roll) =>
      total + currentAllocation(roll).parts.filter((part) => !part.assignedTo).length,
    0,
  );

  const rows = taxRolls
    .filter((roll) => (unassignedOnly ? !isFullyAssigned(roll) : true))
    .filter((roll) => (planFilter ? roll.planNumber === planFilter : true));

  const { sortButton, sortRows } = useGridSort<SortCol, TaxRollRecord>(
    "rollNumber",
    (roll, col) => {
      switch (col) {
        case "assignedTo":
          // Unassigned sorts to the top ascending — the rows that need work.
          return assignedLabel(roll) || " ";
        case "planNumber":
          return Number(roll.planNumber) || 0;
        case "unit":
          return Number(roll.unit) || 0;
        default:
          return roll[col] || "";
      }
    },
  );

  // One-time starting point: the 152 parcels off the 2026 Interim bill, so the
  // register doesn't have to be keyed in by hand. Offered only while the
  // register is empty — after that, rolls are added one at a time.
  const loadSeed = async () => {
    setSeeding(true);
    setNotice(null);
    try {
      const seeded = seedTaxRolls();
      await saveTaxRolls(seeded);

      // Rolls whose number was already typed on a unit come back assigned.
      const assigned = seedAssignmentsFromUnits(seeded, units);
      if (assigned.length > 0) await saveTaxRolls(assigned);

      setNotice(
        `${seeded.length} rolls added` +
          (assigned.length
            ? ` · ${assigned.length} matched to units by their existing roll number`
            : "") +
          ".",
      );
    } catch {
      /* surfaced by the provider */
    } finally {
      setSeeding(false);
    }
  };

  // The modal returns the roll's identity and its allocations together — the
  // Split control edits the latter in place.
  const handleSave = async (
    values: Omit<TaxRollRecord, "id" | "levies" | "budgets">,
  ) => {
    try {
      await saveTaxRolls([
        {
          ...values,
          id: editing?.id || newId("roll"),
          // Correcting a roll's identity mustn't discard what it was billed,
          // nor what's been budgeted against it.
          levies: editing?.levies || [],
          budgets: editing?.budgets || [],
        },
      ]);
      setEditing(undefined);
    } catch {
      /* surfaced by the provider */
    }
  };

  /**
   * A roll a unit is taxed on can't be deleted out from under it — the unit
   * would keep being billed for a parcel with no record behind it, and the next
   * bill would bring the roll back as an unknown.
   *
   * Told rather than greyed out: a disabled button says no without saying why,
   * and the fix is on another screen.
   */
  const requestDelete = (roll: TaxRollRecord) => {
    const held = currentAllocation(roll)
      .parts.filter((part) => part.assignedTo)
      .map((part) => {
        const where = partLabel(part) || "a unit that no longer exists";
        return part.label ? `${part.label} → ${where}` : where;
      });

    if (held.length > 0) {
      setBlockedDelete(
        `Roll ${roll.rollNumber} can't be deleted because it's assigned to ${held.join(
          ", ",
        )}. Remove it on that unit's form first — Units → edit → Add Tax.`,
      );
      return;
    }
    setDeleting(roll);
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await deleteTaxRoll(deleting.id);
      setDeleting(null);
    } catch {
      /* surfaced by the provider */
    }
  };

  return (
    <>
      <div className="tosp-rolls-toolbar">
        <div className="tosp-rolls-counts">
          <strong>{taxRolls.length}</strong> roll{taxRolls.length === 1 ? "" : "s"}
          {unassignedCount > 0 && (
            <span className="tosp-rolls-unassigned-pill">{unassignedCount} unassigned</span>
          )}
        </div>

        <div className="tosp-rolls-controls">
          {plans.length > 1 && (
            <select
              className="manage-input tosp-rolls-filter"
              aria-label="Filter by plan"
              value={planFilter}
              onChange={(e) => setPlanFilter(e.target.value)}
            >
              <option value="">All plans</option>
              {plans.map((plan) => (
                <option key={plan} value={plan}>
                  Plan {plan}
                </option>
              ))}
            </select>
          )}

          <label className="tosp-rolls-toggle">
            <input
              type="checkbox"
              checked={unassignedOnly}
              onChange={(e) => setUnassignedOnly(e.target.checked)}
            />
            Unassigned only
          </label>

          <button type="button" className="tosp-btn-blue" onClick={() => setEditing(null)}>
            Add Tax Roll
          </button>
        </div>
      </div>

      {notice && <p className="tosp-rolls-notice">{notice}</p>}

      {taxRolls.length === 0 ? (
        <div className="tosp-grid-empty">
          <p style={{ margin: "0 0 12px" }}>
            No tax rolls yet. Add them one at a time, or start from the 2026 Interim bill —
            152 parcels across five condo plans and the two land parts.
          </p>
          <button type="button" className="tosp-btn-blue" disabled={seeding} onClick={loadSeed}>
            {seeding ? (
              <>
                <span className="tosp-spinner" aria-hidden="true" />
                Adding…
              </>
            ) : (
              "Load 2026 Interim rolls"
            )}
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div className="tosp-grid-empty">No rolls match the current filters.</div>
      ) : (
        <>
          <div className="tosp-grid-header" style={{ gridTemplateColumns: GRID_COLUMNS }}>
            {COLUMNS.map(({ col, label }) => (
              <div key={col}>{sortButton(col, label)}</div>
            ))}
            <div>Actions</div>
          </div>

          {sortRows(rows).map((roll) => {
            const assigned = assignedLabel(roll);
            return (
              <div
                key={roll.id}
                className="tosp-grid-row"
                title={roll.legalDescription || undefined}
                style={{ gridTemplateColumns: GRID_COLUMNS }}
              >
                <div>
                  {roll.rollNumber}
                  {/* Parcels outside a condo plan — the two land parts — have
                      nothing to put in Plan / Level / Unit, so without this
                      they'd read as two identical rows of dashes. */}
                  {!roll.planNumber && roll.legalDescription && (
                    <span className="tosp-roll-legal">{roll.legalDescription}</span>
                  )}
                </div>
                <div>{roll.planNumber || "—"}</div>
                <div>{roll.level || "—"}</div>
                <div>{roll.unit || "—"}</div>
                <div>{roll.municipalAddress || "—"}</div>
                {/* A split roll lists each part, so a half with no home is
                    visible rather than hidden behind its assigned sibling. */}
                <div className="tosp-roll-parts">
                  {currentAllocation(roll).parts.map((part) => {
                    const where = partLabel(part);
                    return (
                      <div key={part.id}>
                        {part.label && <span className="tosp-part-tag">{part.label}</span>}
                        {where ? (
                          <span
                            className={where === "Missing unit" ? "tosp-roll-orphaned" : undefined}
                          >
                            {where}
                          </span>
                        ) : (
                          <span className="tosp-roll-unassigned">Unassigned</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="tosp-actions">
                  <button
                    type="button"
                    className="tosp-btn-icon tosp-btn-edit"
                    title="Edit"
                    onClick={() => setEditing(roll)}
                  >
                    ✎
                  </button>
                  <button
                    type="button"
                    className="tosp-btn-icon tosp-btn-delete"
                    title={
                      assigned
                        ? "Assigned to a unit — remove it there before deleting"
                        : "Delete"
                    }
                    onClick={() => requestDelete(roll)}
                  >
                    🗑
                  </button>
                </div>
              </div>
            );
          })}
        </>
      )}

      <TaxRollModal
        isOpen={editing !== undefined}
        roll={editing ?? null}
        rolls={taxRolls}
        units={units}
        onClose={() => setEditing(undefined)}
        onSave={handleSave}
      />

      <InfoModal
        isOpen={Boolean(blockedDelete)}
        title="Tax Roll In Use"
        message={blockedDelete || ""}
        onClose={() => setBlockedDelete(null)}
      />

      <DeleteConfirmModal
        isOpen={Boolean(deleting)}
        title="Delete Tax Roll"
        message={
          deleting
            ? `Delete roll ${deleting.rollNumber}? It will come back on the next bill unless the municipality has retired it.`
            : ""
        }
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
      />
    </>
  );
}
